import _ from 'lodash';
import { message } from 'antd';
import { tableConfigForSplitScope } from '../constants/constants';
import { getTable } from '../requests/documentDatas';
import { convertContentToRawText } from '../components/RichEditor/utils';
import {
  initializeBoxContent,
  isATableBox,
  reinitializeBoxWidthAndHeight,
} from './boxes';
import { computeColumnWidth, newColumn } from './columns';
import { newContainer } from './containers';
import { buildUniqueContentData, computeMergedCellContent, transformTextToEditorState } from './dataSync';
import { newTrigger } from './triggers';
import { zoneSplitTableName } from '../shared/tables/zoneSplit';

// `getTableColumnsData` et `applyTableConfigPresentation` vivent désormais dans
// `src/shared/tables/` : le site web en a besoin, et il ne peut pas importer ce
// fichier-ci sans traîner Draft.js et `dataSync`. Réexportés pour que les
// appelants historiques ne bougent pas.
import { getTableColumnsData, catalogFromRawTable, rowKindsOf } from '../shared/tables/tableColumns';
import { applyTableConfigPresentation } from '../shared/tables/tableConfigPresentation';
import { defaultTableConfig } from '../shared/tables/tableDescriptors';
import {
  canGroupColumns,
  canMergeSelection,
  canUngroupColumns,
  canUnmergeSelection,
  computeLabelRowMerges as computeLabelRowMergesShared,
  getLabelRowMergeState as getLabelRowMergeStateShared,
  computeMergedCellLayout,
  groupColumns,
  mergeIntersectsSelection,
  mergedCellSpanKey,
  rectToKeys,
  rowKeysToRect,
  columnKeysToRect,
  isMultiSelectClick,
  removeMergesInSelection,
  resolveMergeAnchor,
  rowGroupKeys,
  selectionHasOnlyManualRows,
  toggleLabelRowOverride,
  ungroupColumns,
} from '../shared/tables/tableStructure';
import { computeRowStyles as computeRowStylesShared } from '../shared/tables/tableRowStyle';
import { withStructureRowKinds as withStructureRowKindsShared } from '../shared/tables/tableRowKinds';

export { getTableColumnsData, applyTableConfigPresentation };
// Style des cases : partagé avec l'aperçu du Drawer (cf. tableCellStyle).
export {
  DEFAULT_TABLE_BORDER_WIDTH,
  tableBorderStyle,
  convertPadding,
  getCellStyle,
  getHeaderStyle,
} from '../shared/tables/tableCellStyle';

// Transforme la réponse brute du backend (table = { rows_*, columns_informations })
// en contenus de box prêts à l'emploi : colonnes + data + disposition, présentation
// (tableConfig) appliquée. Réutilisé par le fetch réseau ET la réapplication locale
// (renommage/masquage/… sans requête). Ne mute pas `table` (getTableColumnsData
// recopie les rows) -> le cache brut reste réutilisable.
export const computeTableBoxContents = (
  table,
  { width, tableConfig, tableName = null, split, refresh, currentIdentifiers, hideField, hideInForm, scopedRowId = null }
) => {
  let rowKeys = Object.keys(table).filter((k) => k.startsWith('rows'));
  // Tableau récursif re-fetché pour UNE seule ligne bouclée (recursiveRowId) :
  // ne garder que la slice correspondante, le reste de `table` couvre les
  // autres itérations de la boucle.
  if (scopedRowId != null) {
    rowKeys = rowKeys.filter((k) => k === `rows_${String(scopedRowId)}`);
  }
  const isEmpty =
    rowKeys.length === 0 ||
    (rowKeys.length === 1 && table[rowKeys[0]].length === 0);
  if (isEmpty) return { isEmpty: true, contents: [] };

  // Les identifiants de la box ne s'appliquent qu'à une réponse découpée
  // (rows_<id>). Une réponse non découpée n'a qu'une slice `rows`, qu'on garde
  // telle quelle et qui ne laisse aucun identifiant sur la box. Sans ça, une
  // box legacy restée avec l'identifiant d'une zone alors qu'elle n'est plus
  // découpée écartait toutes les lignes : plus rien ne se rafraîchissait.
  // Hors rafraîchissement (nouveau tableau choisi), l'ancien identifiant ne vaut
  // plus rien : chaque slice reprend le sien, sinon toutes les box créées par
  // zone héritaient de la même zone.
  const responseIsSplit = rowKeys.some((k) => k.startsWith('rows_'));
  const identifiersOf = (key) =>
    key.replace('rows_', '').replace('rows', '').split('_');
  const keepIdentifiers = refresh && responseIsSplit ? currentIdentifiers : undefined;

  const contents = rowKeys
    .filter(
      (key) =>
        !keepIdentifiers ||
        keepIdentifiers.toString() === identifiersOf(key).toString()
    )
    .map((key) => ({
      ...applyTableConfigPresentation(
        getTableColumnsData(
          {
            columns_informations: table.columns_informations,
            rows: table[key],
            kinds: rowKindsOf(table, key),
          },
          width
        ),
        tableConfig,
        width,
        tableName
      ),
      hideField,
      hideInForm,
      identifiers:
        split && responseIsSplit && !keepIdentifiers
          ? identifiersOf(key)
          : keepIdentifiers,
    }));
  return { isEmpty: false, contents };
};

// Requête qui relit une box tableau : nom, découpe et config envoyés. Partagée
// par fetchTableData et les relectures sans mise à jour de la box.
export const tableFetchArgs = (box, { split = false, recursiveRowId = null } = {}) => {
  const { tableName, tableConfig } = box;
  const scopedRowId = recursiveRowId ?? box.content?.recursiveRowId ?? null;
  const effectiveSplit = split || scopedRowId != null;
  // Box d'une section récursive (scopedRowId) : la découpe est imposée par la
  // boucle, donc les axes qui changeraient l'entité de découpe ne partent pas.
  // Sans ce filtre, un BPU réglé mode=work_positions posé dans une boucle de
  // profils recevait des clés rows_<work_position_id>, le filtre de
  // fetchTableData n'en gardait aucune et la box était vidée ("Table is empty")
  // au premier réglage fait dans le Drawer.
  // Découpage par zone d'un tableau historique : la box garde son nom, la
  // requête vise la variante « par zones ». Une box de section récursive est
  // découpée par la boucle, sur la table elle-même.
  return {
    scopedRowId,
    effectiveSplit,
    fetchName: effectiveSplit && scopedRowId == null ? zoneSplitTableName(tableName) : tableName,
    fetchTableConfig: scopedRowId != null ? tableConfigForSplitScope(tableName, tableConfig) : tableConfig,
  };
};

export const fetchTableData = async ({
  fetchStoreFunc,
  updateBoxFunc,
  box,
  isNewBox = false,
  split = false,
  refresh = false,
  onRawTable = null,
  force = false,
  recursiveRowId = null,
}) => {
  const { tableName, proposal, width, id, tableConfig } = box;
  const { scopedRowId, effectiveSplit, fetchName, fetchTableConfig } = tableFetchArgs(box, {
    split,
    recursiveRowId,
  });
  // `force` = détruire la capture planning existante et la refaire. Réservé aux
  // gestes explicites de rafraîchissement (bouton d'un tableau, rechargement des
  // variables) : c'est le seul cas où l'image affichée peut être périmée.
  //
  // Auparavant tout fetch d'un tableau planning forçait la régénération. Ouvrir
  // le Drawer de configuration suffisait alors à détruire l'image, la faire
  // regénérer, puis la re-télécharger — la ligne s'effondrait et resautait à
  // chaque ouverture. Une capture manquante reste produite sans `force` : le
  // backend l'enfile de lui-même (enqueue_generation_if_needed), et changer
  // d'orientation ou de style vise de toute façon une autre capture.
  updateBoxFunc(id, (boxToUpdate) => {
    boxToUpdate.isFetching = true;
  });
  // Une requête en échec n'est pas un tableau vide : la box garde ce qu'elle
  // affiche, au lieu d'être vidée.
  let table;
  try {
    table = await fetchStoreFunc(fetchName, proposal, {
      split: effectiveSplit,
      force,
      throwOnError: true,
      tableConfig: fetchTableConfig,
    });
  } catch (error) {
    message.error({
      content:
        "Le tableau n'a pas pu être mis à jour, son contenu est conservé. Réessayez dans quelques instants.",
      key: `table-fetch-${id}`,
    });
    return [];
  } finally {
    updateBoxFunc(id, (boxToUpdate) => {
      boxToUpdate.isFetching = false;
    });
  }
  if (onRawTable && table) onRawTable(table);
  if (!table) {
    console.warn(`Table ${tableName} does not exist on the server`);
    table = {};
  }
  let rowKeys = Object.keys(table).filter((k) => k.startsWith('rows'));
  if (scopedRowId != null) {
    rowKeys = rowKeys.filter((k) => k === `rows_${String(scopedRowId)}`);
  }
  if (
    rowKeys.length === 0 ||
    (rowKeys.length === 1 && table[rowKeys[0]].length === 0)
  ) {
    console.warn(`Table ${tableName} is empty`);
    updateBoxFunc(id, (boxToUpdate) => {
      if (isNewBox) {
        return {
          ...boxToUpdate,
          content: {
            ...boxToUpdate.content,
            columns: [],
            hiddenColumnsKeys: [],
            columnsDisposition: [],
            data: [],
            mergedCells: [],
            shouldSplit: effectiveSplit,
            ...(effectiveSplit ? {} : { identifiers: undefined }),
            ...(scopedRowId != null ? { recursiveRowId: scopedRowId } : {}),
          },
          tableName,
          proposal,
        };
      } else {
        boxToUpdate.content.columns = [];
        boxToUpdate.content.columnsDisposition = [];
        boxToUpdate.content.data = [];
        boxToUpdate.content.mergedCells = [];
        boxToUpdate.tableName = tableName;
        boxToUpdate.proposal = proposal;
        boxToUpdate.content.shouldSplit = effectiveSplit;
        if (!effectiveSplit) delete boxToUpdate.content.identifiers;
        if (scopedRowId != null) boxToUpdate.content.recursiveRowId = scopedRowId;
      }
    });
    return [];
  } else {
    const { contents: nextBoxContents } = computeTableBoxContents(table, {
      width,
      tableConfig,
      tableName,
      split: effectiveSplit,
      refresh,
      currentIdentifiers: box.content?.identifiers,
      hideField: box.hideField,
      hideInForm: box.hideInForm,
      scopedRowId,
    });
    // Box découpée dont la zone n'existe plus dans la réponse (zone supprimée
    // ou recréée) : on garde ce qu'elle affiche plutôt que de planter dans
    // buildUniqueContentData, ce qui laissait la box figée sans rien signaler.
    if (nextBoxContents.length === 0) {
      console.warn(
        `Table ${tableName}: no slice matches box ${id} identifiers`,
        { identifiers: box.content?.identifiers, rowKeys }
      );
      return [];
    }
    updateBoxFunc(id, (boxToUpdate) => {
      const { columns, data, columnsDisposition } = buildUniqueContentData(
        nextBoxContents[0]
      );
      if (isNewBox) {
        return {
          ...boxToUpdate,
          content: {
            ...boxToUpdate.content,
            columns,
            data,
            columnsDisposition,
            // Les lignes sont remplacées, clés comprises : une fusion manuelle
            // ne désignerait plus rien.
            mergedCells: [],
            identifiers: nextBoxContents[0].identifiers,
            shouldSplit: effectiveSplit,
            ...(scopedRowId != null ? { recursiveRowId: scopedRowId } : {}),
          },
          tableName,
          proposal,
        };
      } else {
        boxToUpdate.content.columns = columns;
        boxToUpdate.content.columnsDisposition = columnsDisposition;
        boxToUpdate.content.data = data;
        boxToUpdate.content.mergedCells = [];
        boxToUpdate.content.identifiers = nextBoxContents[0].identifiers;
        boxToUpdate.content.shouldSplit = effectiveSplit;
        if (scopedRowId != null) boxToUpdate.content.recursiveRowId = scopedRowId;
        boxToUpdate.tableName = tableName;
        boxToUpdate.proposal = proposal;
      }
    });
    nextBoxContents.shift();
    return nextBoxContents;
  }
};

// Réapplique une config de PRÉSENTATION (colonnes : renommer/masquer/réordonner/
// largeur) sur une box tableau déjà chargée, à partir de la réponse brute mise en
// cache — SANS requête backend. Les axes DONNÉE (mode/consolidation/variante) ne
// passent pas ici (ils changent les rows -> refetch, cf. applyTableConfig).
// Une seule slice traitée (celle de la box, via ses identifiers) : le split reste
// géré box par box comme au fetch.
export const reapplyTableConfigLocally = ({
  table,
  box,
  tableConfig,
  updateBoxFunc,
  split = false,
}) => {
  const { id, width } = box;
  const { isEmpty, contents } = computeTableBoxContents(table, {
    width,
    tableConfig,
    tableName: box.tableName,
    split,
    refresh: true,
    currentIdentifiers: box.content?.identifiers,
    hideField: box.hideField,
    hideInForm: box.hideInForm,
  });

  // Aucune slice ne correspond aux identifiants (zone disparue) : même règle
  // qu'au fetch, la box garde son contenu.
  if (!isEmpty && contents.length === 0) {
    console.warn(`Table ${box.tableName}: no slice matches box ${id} identifiers`);
    return;
  }

  if (isEmpty) {
    updateBoxFunc(id, (boxToUpdate) => {
      boxToUpdate.content.columns = [];
      boxToUpdate.content.columnsDisposition = [];
      boxToUpdate.content.data = [];
      boxToUpdate.content.mergedCells = [];
    });
    return;
  }

  updateBoxFunc(id, (boxToUpdate) => {
    const { columns, data, columnsDisposition } = buildUniqueContentData(
      contents[0]
    );
    boxToUpdate.content.columns = columns;
    boxToUpdate.content.columnsDisposition = columnsDisposition;
    boxToUpdate.content.data = data;
    boxToUpdate.content.mergedCells = [];
    boxToUpdate.content.identifiers = contents[0].identifiers;
    boxToUpdate.content.shouldSplit = split;
  });
};

export const convertMinHeight = (size) => {
  if (size === 'middle') {
    return 48;
  } else if (size === 'small') {
    return 40;
  } else {
    return 56;
  }
};

export const convertMargin = (size) => {
  let marginInfos = {};
  if (size === 'middle') {
    marginInfos = { horizontal: 0, vertical: 8 };
  } else if (size === 'small') {
    marginInfos = { horizontal: 0, vertical: 4 };
  } else {
    marginInfos = { horizontal: 0, vertical: 12 };
  }
  marginInfos.margin = `${marginInfos.vertical}px ${marginInfos.horizontal}px`;
  return marginInfos;
};

export const getTableRowHeight = (size) => {
  let baseHeight = 32;
  const { vertical: marginVertical } = convertMargin(size);
  return Math.max(baseHeight, baseHeight + marginVertical * 2);
};

export const fillEmptyAdminTablesBoxes = async ({
  boxes,
  proposable_id,
  type,
  containers,
  columns,
  gridConfiguration,
  configuration,
  landscape,
  marginRight,
  marginLeft,
  header,
  footer,
  triggers,
  variableOrderArray,
}) => {
  const newContainers = [...containers];
  const newColumns = [...columns];
  const newBoxes = [];
  for (const b of boxes ?? []) {
    if (!isATableBox(b.type) || b.content?.data?.length !== 0) {
      newBoxes.push(b);
      continue;
    }

    let newBox = { ...b };
    const newBoxTriggers = triggers?.[b.id] ?? [];
    const shouldSplit = !!newBox.content?.shouldSplit;
    const nextBoxContents = await fetchTableData({
      fetchStoreFunc: async (tableName, _proposal, { split, tableConfig, force, throwOnError }) => {
        const table = await getTable({ id: proposable_id, type }, tableName, {
          split,
          tableConfig,
          force,
          throwOnError,
        });
        return table;
      },
      updateBoxFunc: (id, callback) => {
        const result = callback(newBox);
        if (result !== undefined) {
          newBox = result;
        }
      },
      box: newBox,
      isNewBox: true,
      split: shouldSplit,
    });
    newBoxes.push(newBox);
    const boxContainerIdx = newContainers.findIndex((c) =>
      c.columnsIds.includes(newBox.columnId)
    );
    const boxColumnIdx = newColumns.findIndex((c) => c.id === newBox.columnId);
    const vOrderIdx = variableOrderArray.findIndex(
      ({ id }) => id === newBox.id
    );
    for (const nextBoxContent of nextBoxContents.reverse()) {
      const { columns, data, columnsDisposition } =
        buildUniqueContentData(nextBoxContent);
      const box = {
        type: newBox.type,
        ...gridConfiguration[newBox.type],
        copied: false,
      };
      let nextTableBox = initializeBoxContent({
        box,
        configuration,
      });
      nextTableBox = reinitializeBoxWidthAndHeight({
        box: nextTableBox,
        columnWidth: computeColumnWidth({
          landscape,
          marginRight,
          marginLeft,
        }),
        onCreate: true,
        header,
        footer,
      });
      // gridConfiguration template may be Immer-frozen; replace content instead of mutating
      nextTableBox = {
        ...nextTableBox,
        hideField: nextBoxContent.hideField,
        hideInForm: nextBoxContent.hideInForm,
        name: newBox.name,
        content: {
          ...nextTableBox.content,
          columns,
          columnsDisposition,
          data,
          identifiers: nextBoxContent.identifiers,
          shouldSplit: true,
        },
        defaultValue: newBox.defaultValue,
        tableName: newBox.tableName,
        proposal: newBox.proposal,
        tableConfig: newBox.tableConfig,
      };
      const newColumnToInsert = newColumn({});
      const newContainerToInsert = newContainer({
        height: newContainers[boxContainerIdx].height,
        columnsIds: [newColumnToInsert.id],
        editableConfiguration: newContainers[boxContainerIdx].editable,
      });
      nextTableBox.columnId = newColumnToInsert.id;
      newColumns.splice(boxColumnIdx + 1, 0, newColumnToInsert);
      newContainers.splice(boxContainerIdx + 1, 0, newContainerToInsert);
      triggers[nextTableBox.id] = newBoxTriggers.map((trigger) =>
        newTrigger({
          triggerType: trigger.triggerType,
          triggerId: trigger.triggerId,
          value: trigger.value,
          result: trigger.result,
        })
      );
      variableOrderArray.splice(vOrderIdx + 1, 0, {
        id: nextTableBox.id,
        containerId: newContainerToInsert.id,
      });
      newBoxes.push(nextTableBox);
    }
  }
  return {
    boxes: newBoxes,
    containers: newContainers,
    columns: newColumns,
    triggers: triggers,
    variableOrderArray: variableOrderArray,
  };
};

// ---------------------------------------------------------------------------
// Fusion de cellules et groupes de colonnes
// ---------------------------------------------------------------------------
// Le moteur vit dans `src/shared/tables/tableStructure.js` : le site web en a
// besoin, et il ne peut pas importer ce fichier-ci sans traîner Draft.js. Seule
// la définition de « cellule vide » reste ici, parce qu'elle, elle connaît
// Draft — les cellules vides d'un tableau variable sont souvent un état Draft
// sérialisé, pas une chaîne vide.

export {
  canGroupColumns,
  canMergeSelection,
  canUngroupColumns,
  canUnmergeSelection,
  computeMergedCellLayout,
  groupColumns,
  mergeIntersectsSelection,
  mergedCellSpanKey,
  rectToKeys,
  rowKeysToRect,
  columnKeysToRect,
  isMultiSelectClick,
  removeMergesInSelection,
  resolveMergeAnchor,
  rowGroupKeys,
  selectionHasOnlyManualRows,
  toggleLabelRowOverride,
  ungroupColumns,
};

const isEmptyCellValue = (value) => {
  if (value == null) return true;
  if (typeof value !== 'string') return !value;
  const trimmed = value.trim();
  if (trimmed === '') return true;
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return convertContentToRawText(transformTextToEditorState(value)).trim() === '';
    } catch (e) {
      return false;
    }
  }
  return false;
};

// Texte nu d'une cellule ou d'un titre de colonne (souvent du Draft
// sérialisé) : c'est par lui qu'une exception de fusion retrouve sa
// ligne-titre (cf. resolveLabelRowMerge), et une colonne son libellé d'origine.
export const tablePlainText = (value) => {
  if (value == null) return '';
  if (typeof value !== 'string') return String(value);
  const trimmed = value.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return convertContentToRawText(transformTextToEditorState(value)).trim();
    } catch (e) {
      return trimmed;
    }
  }
  return trimmed;
};

// Seuls les réglages de rendu diffèrent, la fusion (`merge`) ou le style des
// lignes (`rowStyles`) ? Ils se lisent au rendu : rien à relire ni à
// rematérialiser. Une config identique renvoie false et garde sa réapplication
// habituelle (le canvas s'y fie pour recaler une largeur glissée).
// Un tableau jamais configuré n'a pas de config précédente : le Drawer part de
// celle par défaut du tableau, c'est donc elle qu'on compare. Sans ça, la
// première fusion relirait tout le tableau et remplacerait ses lignes.
export const tableConfigOnlyRenderingChanged = (prevConfig, nextConfig, tableName = null) => {
  const prev = prevConfig ?? defaultTableConfig(tableName);
  // Une liste de colonnes vide veut dire « catalogue d'origine » : comme absente.
  const presentation = (config) => {
    const { merge, rowStyles, columns, ...rest } = config ?? {};
    return columns?.length ? { ...rest, columns } : rest;
  };
  return (
    ['merge', 'rowStyles'].some((field) => !_.isEqual(prev?.[field], nextConfig?.[field])) &&
    _.isEqual(presentation(prev), presentation(nextConfig))
  );
};

// Libellé d'ORIGINE d'une colonne (celui de l'API), clé des overrides de
// tableConfig.columns. `originalTitle` n'est posé que par la présentation ;
// sans lui, c'est le titre — mais en texte nu : au chargement du document les
// titres passent en Draft (replacePlaceholderTable). Un titre déjà renommé au
// canvas avant toute config n'est plus le libellé de l'API : la réponse brute
// (`rawTable`) le retrouve par position. null si rien ne le donne sûrement.
export const columnOriginLabel = (box, columnKey, rawTable) => {
  const columns = box.content?.columns ?? [];
  const index = columns.findIndex((c) => c.key === columnKey);
  if (index < 0) return null;
  const column = columns[index];
  if (column.originalTitle) return column.originalTitle;
  const text = tablePlainText(column.title);
  if (!rawTable) return text || null;
  const rawLabels = catalogFromRawTable(rawTable, box.content?.identifiers).map((c) => c.label);
  if (rawLabels.includes(text)) return text;
  // Position fiable seulement si aucune colonne n'est masquée ni déplacée.
  return rawLabels.length === columns.length ? rawLabels[index] : null;
};

export const originalColumnLabel = (col) => col.originalTitle ?? tablePlainText(col.title);

// `mergeOptions` : réglages `tableConfig.merge` de la box. Les bornes d'une
// fusion (`from` / `to`) visent le libellé d'origine d'une colonne.
export const computeLabelRowMerges = (data, columns, mergeOptions) =>
  computeLabelRowMergesShared(data, columns, isEmptyCellValue, {
    ...mergeOptions,
    getText: tablePlainText,
    columnLabel: originalColumnLabel,
  });

export const getLabelRowMergeState = (row, columns, mergeOptions) =>
  getLabelRowMergeStateShared(row, columns, isEmptyCellValue, {
    ...mergeOptions,
    getText: tablePlainText,
  });

// Lignes du document avec leur type déduit de la structure du tableau (cf.
// tableRowKinds), lues à travers le Draft.
export const withStructureRowKinds = (data, columns, structure) =>
  withStructureRowKindsShared(data, columns, structure, {
    isEmptyValue: isEmptyCellValue,
    getText: tablePlainText,
    columnLabel: originalColumnLabel,
  });

// Règle de style de chaque ligne (`tableConfig.rowStyles`), par clé de ligne :
// mêmes lignes que la fusion auto, lues à travers le Draft du document.
export const computeRowStyles = (data, columns, rowStyles, structure) =>
  computeRowStylesShared(data, columns, isEmptyCellValue, {
    rowStyles,
    getText: tablePlainText,
    structure,
  });

// Style des lignes d'un tableau custom, rangé sur la ligne elle-même
// (`row.rowStyle`, réglé dans « Configurer le tableau ») : il la suit quand on
// la déplace, la copie ou la supprime. Par clé de ligne, comme computeRowStyles.
export const customRowStyles = (data) => {
  const byKey = new Map();
  (data ?? []).forEach((row) => {
    if (row.rowStyle) byKey.set(row.key, row.rowStyle);
  });
  return byKey;
};

// Fusionne les cases `keys` ({ rowKeys, columnKeys }, cf. rectToKeys) du
// contenu d'une box en cours de modification. Le contenu des cases est réuni
// dans la case haut-gauche (cf. computeMergedCellContent), les autres sont
// vidées : il ne vit plus qu'à un endroit, la défusion n'a rien à dédoubler.
export const mergeTableCells = (content, keys) => {
  const rows = content.data;
  const anchorRowKey = keys.rowKeys[0];
  const anchorColKey = keys.columnKeys[0];
  const values = [];
  keys.rowKeys.forEach((rowKey) => {
    const row = rows.find((r) => r.key === rowKey);
    if (!row) return;
    keys.columnKeys.forEach((columnKey) => values.push(row[columnKey]));
  });
  const combined = computeMergedCellContent(values);
  keys.rowKeys.forEach((rowKey) => {
    const row = rows.find((r) => r.key === rowKey);
    if (!row) return;
    keys.columnKeys.forEach((columnKey) => {
      if (rowKey !== anchorRowKey || columnKey !== anchorColKey) row[columnKey] = '';
    });
  });
  const anchorRow = rows.find((r) => r.key === anchorRowKey);
  if (anchorRow) anchorRow[anchorColKey] = combined;
  content.mergedCells = [
    ...removeMergesInSelection(content.mergedCells ?? [], keys),
    { rowKeys: [...keys.rowKeys], columnKeys: [...keys.columnKeys] },
  ];
};

// Défusion « façon tableur » : le contenu (édits inclus) reste dans la case
// haut-gauche, les autres restent vides. On retire juste la fusion.
export const unmergeTableCells = (content, keys) => {
  if (!Array.isArray(content.mergedCells)) return;
  content.mergedCells = removeMergesInSelection(content.mergedCells, keys);
};

// Alignement d'une colonne entière (`field` : textAlign | verticalAlign) : il
// passe sur toutes ses cases, et sur son titre.
export const alignTableColumn = (content, columnKey, field, value) => {
  const column = content.columns.find((c) => c.key === columnKey);
  if (!column) return;
  column[field] = value;
  if (field === 'textAlign') column.headerTextAlign = undefined;
  content.data.forEach((row) => {
    row[`${field}_${columnKey}`] = undefined;
  });
};
